/**
 * Pure helpers for FilterBar: checklist transform, concepts/keywords total, array compare.
 * Extracted for unit testing and reuse.
 */
import type { Dayjs } from "dayjs";
import type { AssessmentChecklistItem } from "../../../services/assessmentService";
import {
  type ConceptValue,
  type UserFilter,
  getConceptValues,
} from "../../../services/regulationsService";
import type { ImpactAssessmentChecklistFormValue } from "../../ImpactAssessmentChecklistForm";

/** Check if assessment API response indicates an error */
export function isAssessmentApiError(res: {
  status?: string;
  code?: number;
}): boolean {
  return res.status !== "success" || (res.code !== undefined && res.code >= 400);
}

export type FilterFormOriginalData = {
  markets: ConceptValue[];
  productTypes: ConceptValue[];
  ha: ConceptValue[];
  tags: ConceptValue[];
  searchTerm: string;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
};

/** Check if current filter form differs from original applied filter data */
export function isFilterFormModified(
  current: {
    markets: ConceptValue[];
    productTypes: ConceptValue[];
    ha: ConceptValue[];
    tags: ConceptValue[];
    searchTerm: string;
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
  },
  original: FilterFormOriginalData,
): boolean {
  return (
    !arraysEqual(current.markets, original.markets) ||
    !arraysEqual(current.productTypes, original.productTypes) ||
    !arraysEqual(current.ha, original.ha) ||
    !arraysEqual(current.tags, original.tags) ||
    current.searchTerm !== original.searchTerm ||
    current.fromDate?.format("YYYY-MM-DD") !==
      original.fromDate?.format("YYYY-MM-DD") ||
    current.toDate?.format("YYYY-MM-DD") !==
      original.toDate?.format("YYYY-MM-DD")
  );
}

/**
 * Merge options with selected items that may not be in options yet (by code).
 * When `searchInput` is non-empty (after trim), only the current `options` list is used (e.g. search
 * results); selected values not in that list are not prepended — chips still show via Autocomplete value.
 * When search is empty, missing selected are prepended so users can see/remove selections not on the current page.
 */
export function mergeOptionsWithSelected(
  options: ConceptValue[],
  selected: ConceptValue[],
  searchInput?: string | null,
): ConceptValue[] {
  const opts = Array.isArray(options) ? options : [];
  const searchActive = (searchInput ?? "").trim().length > 0;

  if (searchActive) {
    const seen = new Set<string>();
    return opts.filter((x) => {
      if (seen.has(x.code)) return false;
      seen.add(x.code);
      return true;
    });
  }

  const optionCodes = new Set(opts.map((x) => x.code));
  const missingSelected = selected.filter((x) => !optionCodes.has(x.code));
  const merged = [...missingSelected, ...opts];
  const seen = new Set<string>();
  return merged.filter((x) => {
    if (seen.has(x.code)) return false;
    seen.add(x.code);
    return true;
  });
}

/** Fetch concept values and build code→name map for given codes */
export async function fetchConceptMapForCodes(
  conceptName: string,
  codes: Set<string>,
): Promise<Map<string, string>> {
  if (codes.size === 0) return new Map();
  const map = new Map<string, string>();
  try {
    const response = await getConceptValues(conceptName, undefined, {
      page: 1,
      page_size: 500,
    });
    const items = Array.isArray(response?.data) ? response.data : [];
    if (response?.code === 200) {
      items.forEach((item) => {
        if (codes.has(item.code)) map.set(item.code, item.name ?? item.code);
      });
    }
    const missing = [...codes].filter((c) => !map.has(c));
    for (const code of missing) {
      try {
        const searchRes = await getConceptValues(conceptName, code, {
          page: 1,
          page_size: 20,
          searchBy: "code",
        });
        const searchItems = Array.isArray(searchRes?.data) ? searchRes.data : [];
        const found = searchItems.find((item) => item.code === code);
        if (found) map.set(code, found.name ?? code);
      } catch {
        // Keep code out of map; mergeConceptsWithMap will fall back to code
      }
    }
    return map;
  } catch {
    return map;
  }
}

/** Fetch concept values and filter to those matching given codes */
export async function fetchConceptsByCodes(
  conceptName: string,
  codes: string[],
): Promise<ConceptValue[]> {
  if (!codes?.length) return [];
  const codeSet = new Set(codes);
  const result: ConceptValue[] = [];
  const response = await getConceptValues(conceptName, undefined, {
    page: 1,
    page_size: 500,
  });
  const items = Array.isArray(response?.data) ? response.data : [];
  if (response?.code === 200) {
    items.forEach((item) => {
      if (codeSet.has(item.code)) result.push(item);
    });
  }
  const missing = codes.filter((c) => !result.some((r) => r.code === c));
  for (const code of missing) {
    try {
      const searchRes = await getConceptValues(conceptName, code, {
        page: 1,
        page_size: 20,
        searchBy: "code",
      });
      const searchItems = Array.isArray(searchRes?.data) ? searchRes.data : [];
      const found = searchItems.find((item) => item.code === code);
      if (found) result.push(found);
    } catch {
      // Skip; mergeConceptsWithMap will use map or code
    }
  }
  return result;
}

/** Merge fetched ConceptValues with code→name map for codes not in first page */
export function mergeConceptsWithMap(
  codes: string[],
  fetched: ConceptValue[],
  codeToName: Map<string, string>,
): ConceptValue[] {
  return codes.map((code) => {
    const found = fetched.find((f) => f.code === code);
    if (found) return found;
    return { code, name: codeToName.get(code) ?? code };
  });
}

/** Format a criteria part (e.g. "Markets: A, B") from codes and code→name map */
export function formatCriteriaPart(
  label: string,
  codes: string[] | undefined,
  codeToName: Map<string, string>,
): string | null {
  if (!codes?.length) return null;
  const names = codes.map((c) => codeToName.get(c) ?? c).join(", ");
  return `${label}: ${names}`;
}

/** Format date range for display */
export function formatDateRangeDisplay(
  fromDate: Dayjs | null,
  toDate: Dayjs | null,
): string {
  if (fromDate && toDate) {
    return `${fromDate.format("DD-MMM-YYYY")} - ${toDate.format(
      "DD-MMM-YYYY",
    )}`;
  }
  if (fromDate) return `From: ${fromDate.format("DD-MMM-YYYY")}`;
  if (toDate) return `To: ${toDate.format("DD-MMM-YYYY")}`;
  return "";
}

export type FilterBarChecklistItem = {
  id: string;
  name: string;
  description: string;
  conceptsCount: number;
  dimensionsCount: number;
  keywordsCount: number;
  is_favorite: boolean;
  raw: AssessmentChecklistItem;
};

/**
 * Map API checklist item to FilterBar card shape (conceptsCount = total concept values, dimensionsCount = concept groups).
 */
export function transformChecklistToFilterBarItem(
  item: AssessmentChecklistItem,
): FilterBarChecklistItem {
  const concepts = item.concepts?.concepts ?? [];
  const conceptsValuesCount = concepts.reduce(
    (sum, c) => sum + (Array.isArray(c.values) ? c.values.length : 0),
    0,
  );
  return {
    id: String(item.id),
    name: item.name,
    description: item.description,
    conceptsCount: conceptsValuesCount,
    dimensionsCount: concepts.length,
    keywordsCount: Array.isArray(item.keywords) ? item.keywords.length : 0,
    is_favorite: item.is_favorite ?? false,
    raw: item,
  };
}

/**
 * Total of selected concept ids count + comma-separated keywords count (for checklist save validation).
 */
export function getConceptsKeywordsTotal(
  form: ImpactAssessmentChecklistFormValue,
): number {
  const concepts = form.selectedConceptIds.length;
  const keywords = form.keywords.trim()
    ? form.keywords
        .trim()
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean).length
    : 0;
  return concepts + keywords;
}

/**
 * Compare two ConceptValue arrays by .code (order-independent).
 */
export function arraysEqual(a: ConceptValue[], b: ConceptValue[]): boolean {
  if (a.length !== b.length) return false;
  const aCodes = new Set(a.map((x) => x.code));
  return b.every((x) => aCodes.has(x.code));
}

/** Count user-created saved filters (excludes built-in presets with negative ids). */
export function countUserSavedFilters(
  savedFilters: readonly UserFilter[],
): number {
  return savedFilters.filter((f) => f.id >= 0).length;
}

/** Whether user-created saved filters reached max_user_filters from global config. */
export function isMaxUserSavedFiltersReached(
  savedFilters: readonly UserFilter[],
  maxUserFilters: number,
): boolean {
  return countUserSavedFilters(savedFilters) >= maxUserFilters;
}

/** Resolve the user filter row created by a save-as-new action. */
export function findNewlyCreatedUserFilter(
  filters: UserFilter[],
  previousUserFilterIds: ReadonlySet<number>,
  filterName: string,
): UserFilter | undefined {
  const userFilters = filters.filter((f) => f.id >= 0);
  const newlyAdded = userFilters.filter((f) => !previousUserFilterIds.has(f.id));
  if (newlyAdded.length === 1) return newlyAdded[0];
  const matchingName = userFilters.filter((f) => f.name === filterName);
  if (matchingName.length === 0) return undefined;
  return matchingName.reduce(
    (latest, f) => (f.id > latest.id ? f : latest),
    matchingName[0],
  );
}
